import { BuildingType, type BuildingTypeId, dollars } from './buildings';
import type { Command } from './commands';
import { TICKS_PER_GAME_HOUR } from './constants';
import { NetworkKind } from './ground';
import { World } from './world';

/*
 * A ready-made airport to start from: a lit runway with a parallel taxiway, five stands, a
 * terminal with its kerb and car parks, the fire station, ground handling, fuel, a hangar, a
 * cargo terminal and a hotel, roads to the motorway, and the first airline contracts signed. Built with the
 * same commands a player would send (construction is finished at once), so it is a valid world
 * like any other: deterministic for a seed, and saved and shared in co-op as usual.
 *
 * Map: the default 256×256 with the starting land (x 64–159, y 96–159) and the strip of land
 * north of it (y 64–95) bought for the airside. The motorway runs along y 160.
 */

/** Map sizes a new game can be played on (tiles a side). */
export const MAP_SIZES = { normal: 256, large: 384 } as const;
export type MapSize = keyof typeof MAP_SIZES;

/**
 * An empty world of the given size, named like a new game. The starting land sits in the middle
 * whatever the size; a large map simply has more land around it to buy.
 */
export function createWorldOfSize(seed: number, size: MapSize, name = '', theme = 0): World {
  const side = MAP_SIZES[size];
  const world = World.create({ seed, width: side, height: side, theme });
  if (name) {
    world.step({
      seq: world.seq,
      advance: false,
      commands: [{ playerId: 0, command: { type: 'renameAirport', name } }],
    });
    world.drainEvents();
  }
  return world;
}

/** Money left to the players once the demo airport stands. */
export const DEMO_MONEY = dollars(1_500_000);
/** Airline contracts signed before play starts. */
const DEMO_CONTRACTS = 5;

export function createDemoWorld(seed: number, name = '', theme = 0): World {
  const world = World.create({ seed, startingMoney: dollars(80_000_000), theme });
  const send = (command: Command) => {
    world.step({ seq: world.seq, advance: false, commands: [{ playerId: 0, command }] });
    for (const e of world.drainEvents()) {
      if (e.type === 'commandRejected') {
        throw new Error(`demo airport: ${command.type} rejected (${e.reason})`);
      }
    }
  };
  const place = (building: BuildingTypeId, x: number, y: number, rot = 0) => {
    send({ type: 'placeBuilding', building, x, y, rot });
    world.completeConstruction();
    return world.buildings[world.buildings.length - 1]?.id ?? 0;
  };
  const upgrade = (id: number, level: number) => {
    for (let b = world.buildings.find((k) => k.id === id); b && b.level < level; ) {
      send({ type: 'upgradeBuilding', id });
      world.completeConstruction();
    }
  };
  const taxiway = (x0: number, y0: number, x1: number, y1: number) => {
    send({ type: 'buildNetwork', kind: NetworkKind.Taxiway, x0, y0, x1, y1 });
    send({ type: 'paintTaxiLine', x0, y0, x1, y1, remove: false });
  };
  const road = (x0: number, y0: number, x1: number, y1: number) =>
    send({ type: 'buildNetwork', kind: NetworkKind.Road, x0, y0, x1, y1 });

  if (name) send({ type: 'renameAirport', name });
  for (let px = 2; px <= 4; px++) send({ type: 'buyParcel', px, py: 2 });

  const tower = place(BuildingType.Tower, 74, 91);
  upgrade(tower, 3);

  // Airside: runway, exits, the parallel taxiway and a row of stands with their links.
  const runway = place(BuildingType.Runway, 80, 66);
  upgrade(runway, 2);
  taxiway(86, 70, 86, 73);
  taxiway(137, 70, 137, 73);
  taxiway(86, 73, 137, 73);
  for (const x of [84, 96, 108, 120, 132]) {
    // Turned round: the lead-in faces the taxiway (north), the jet bridge side the terminal.
    place(BuildingType.Stand, x, 77, 2);
    taxiway(x + 5, 73, x + 5, 76);
  }

  // Service road behind the stands, and roads down to the motorway.
  road(66, 88, 156, 88);
  road(70, 88, 70, 159);
  road(154, 88, 154, 159);
  road(106, 101, 106, 159);

  // Along the service road: fire, ground handling, terminal, fuel, hangar, cargo.
  upgrade(place(BuildingType.FireStation, 80, 91), 2);
  upgrade(place(BuildingType.GroundHandling, 88, 91), 2);
  upgrade(place(BuildingType.Terminal, 100, 91, 2), 2);
  place(BuildingType.FuelDepot, 120, 91);
  place(BuildingType.Hangar, 126, 91);
  place(BuildingType.CargoTerminal, 138, 91);

  // Landside: the taxi and bus station and car parks on the terminal road.
  place(BuildingType.TransitHub, 95, 104);
  place(BuildingType.Hotel, 95, 111);
  place(BuildingType.Parking, 109, 104);
  place(BuildingType.Parking, 109, 113);

  // Let the first airlines call: sign offers as they come, a day at most.
  for (let hour = 0; hour < 30 && world.contracts.length < DEMO_CONTRACTS; hour++) {
    for (let t = 0; t < TICKS_PER_GAME_HOUR; t++) {
      world.step({ seq: world.seq, advance: true, commands: [] });
    }
    world.drainEvents();
    // Mostly passenger routes, with one freight contract at most.
    for (const offer of [...world.offers]) {
      const freight = world.contracts.some((c) => c.cargo);
      const take = !offer.cargo || !freight;
      send({ type: take ? 'acceptOffer' : 'declineOffer', offerId: offer.id });
      if (world.contracts.length >= DEMO_CONTRACTS) break;
    }
  }
  world.money = DEMO_MONEY;
  return world;
}
